/**
 * A widget: what the content area shows for a screen. It declares its props as a schema, so the
 * content module checks them before rendering — bad props are a visible error, not a broken widget.
 *
 * A widget talks back only through `WidgetApi`: send a button, say words, keep a draft.
 */
import type { ComponentType } from 'react';
import type { ZodType } from 'zod';
import type { ScreenContext } from '../packs/contract';

export interface WidgetApi {
  press(button: string, data?: Record<string, unknown>): void;
  say(text: string): void;
  setDraft(patch: Record<string, unknown>): void;
  /** True when the screen is a replay or otherwise read-only: show, don't act. */
  readOnly: boolean;
  /** Swiping allowed (off in motor mode). */
  swipe: boolean;
}

export interface WidgetProps<P> {
  props: P;
  ctx: ScreenContext;
  api: WidgetApi;
}

/**
 * Another look for the same widget and the same props (a plain list, graphic cards…). Which one is
 * drawn: what Settings chose for this screen (`appearance.screenVariants`), else what Settings or the preset chose for
 * this widget (`appearance.widgetVariants`), else the first
 * look the theme prefers that it has (`appearance.preferredVariants`), else what the spec suggested for the
 * screen (`WidgetCall.variant`), else the standard one.
 */
export interface WidgetVariant<P> {
  id: string;
  /** i18n key, shown in Settings. */
  label: string;
  Component: ComponentType<WidgetProps<P>>;
  /**
   * It reaches the screen's edges, whatever the margin from the edges (a carousel whose neighbours
   * peek in from the sides): the content area does not inset it.
   */
  edgeToEdge?: boolean;
}

export interface WidgetDefinition<P> {
  /** `name` or `name@major`: a breaking change of the props is a new major. */
  key: string;
  schema: ZodType<P>;
  /** The standard look. */
  Component: ComponentType<WidgetProps<P>>;
  /** Other looks, each with the same props. */
  variants?: WidgetVariant<P>[];
}

export const STANDARD_VARIANT = 'standard';

/** Helper that keeps the props type of a widget definition. */
export function defineWidget<P>(def: WidgetDefinition<P>): WidgetDefinition<P> {
  return def;
}

/** Every look of a widget, the standard one first. */
export function variantsOf<P>(def: WidgetDefinition<P>): WidgetVariant<P>[] {
  return [{ id: STANDARD_VARIANT, label: 'widget.variant.standard', Component: def.Component }, ...(def.variants ?? [])];
}

/** The first look asked for that the widget has; the standard one otherwise. */
export function pickVariant<P>(def: WidgetDefinition<P>, wanted: (string | undefined)[]): WidgetVariant<P> {
  const all = variantsOf(def);
  for (const id of wanted) {
    const found = id ? all.find((v) => v.id === id) : undefined;
    if (found) return found;
  }
  return all[0]!;
}
